import $ from 'jquery'

import { useDrawer } from '@app/shared/pinia/drawer'
import { isUsingNewLayout } from '@app/adapters/silverfin'

import { useFileAttachmentsLiveInputStore } from '@app/application/sidebar-file-attachments/store/file-attachments-live-input'
import getPiniaInstance from '@app/shared/pinia/instance'
import { RightDrawerContent } from '@app/shared/types/drawer'
import { isUsingTwoDrawerLayout } from '@app/utils/domHelpers'

const activeFileLabelClasses = ['tw-border-solid', 'tw-border-sf-primary']

const PROPS_SEPARATOR = '__separator__'

export default class LiveInputsFileCollectionInput {
  constructor(liveInputs, locked) {
    this.liveInputs = liveInputs
    this.locked = locked
    this.activeFileElementIdentifier = null
    this.clickEventHandler =
      isUsingTwoDrawerLayout() && !liveInputs.fileAttachmentsAsModal
        ? this.openFileAttachmentsDrawer.bind(this)
        : this.openDocumentSelect.bind(this)

    this.handleDrawerClose = this.handleDrawerClose.bind(this)
    this.handleUpdateAttachmentsEvent =
      this.handleUpdateAttachmentsEvent.bind(this)
    this.initializeListeners()
  }

  inputFilter() {
    return "[data-behaviour~='live-inputs-file-collection-input']"
  }

  // for a new file added via direct upload
  attributesForDirectUpload(data) {
    return {
      document_direct_upload_path: data.link,
    }
  }

  // for a new file added from permanent files
  attributesForPermanentDocument(data) {
    return {
      copy_from_permanent_document_id: data.permanent_document_id,
    }
  }

  attributesForExport(data) {
    return {
      copy_from_export_id: data.export_id,
      document_user_file_name: `${data.name}.pdf`,
    }
  }

  // for an existing file
  attributesForDocument(data) {
    return {
      document_id: data.document_id,
    }
  }

  attributesForExternalLink(data) {
    if (data.linkType === 'ExternalLink') {
      return {
        name: data.name,
        url: data.url,
        id: data.document_id,
      }
    }
    return {}
  }

  paramsForDocumentsDownload(documentSpan) {
    return {
      firm_id: Silverfin.currentFirmId,
      company_id: Silverfin.currentCompanyId,
      name: documentSpan.dataset.name,
      object_id: documentSpan.dataset.objectId,
      object_type: documentSpan.dataset.objectType,
      object_ledger_id: documentSpan.dataset.objectLedgerId,
    }
  }

  initializeListeners() {
    this.liveInputs.on('click', this.inputFilter(), this.clickEventHandler)
    window.eventHub.$on('drawer:animation-end', this.handleDrawerClose)
    window.eventHub.$on(
      'attachments-sidebar:update-attachments',
      this.handleUpdateAttachmentsEvent
    )
  }

  handleUpdateAttachmentsEvent({
    targetElement,
    newAttachments,
    attachmentsToDelete,
    successCallback,
    errorCallback,
  }) {
    this.updateAttachments(
      $(targetElement),
      newAttachments,
      attachmentsToDelete,
      successCallback,
      errorCallback
    )
  }

  handleDrawerClose() {
    const drawerStore = useDrawer(getPiniaInstance())
    const content = isUsingNewLayout()
      ? drawerStore.rightContent
      : drawerStore.open.content
    if (
      !drawerStore.isAnyDrawerOpen ||
      content !== RightDrawerContent.FileAttachments
    ) {
      this.clearActiveFileElementIdentifier()
    }
  }

  getDocumentAttributes(attachmentData) {
    const attributes = {
      DirectUpload: this.attributesForDirectUpload(attachmentData),
      PermanentDocument: this.attributesForPermanentDocument(attachmentData),
      Document: this.attributesForDocument(attachmentData),
      Export: this.attributesForExport(attachmentData),
      ExternalLink: this.attributesForExternalLink(attachmentData),
    }
    return attributes[attachmentData.linkType]
  }

  openFileAttachmentsDrawer(event) {
    const $documentSpan = $(event.currentTarget)

    // Reset the potential previously active text_input
    this.liveInputs.setActiveElement($documentSpan)
    const uniqueIdentifier = [
      event.currentTarget.dataset.name,
      event.currentTarget.dataset.objectId,
    ].join(PROPS_SEPARATOR)
    this.setActiveFileElementIdentifier(uniqueIdentifier)

    const drawerStore = useDrawer(getPiniaInstance())
    const fileAttachmentsLiveInputStore = useFileAttachmentsLiveInputStore(
      getPiniaInstance()
    )

    fileAttachmentsLiveInputStore.setActiveFileElementOptions({
      textPropertyName: $documentSpan[0].dataset.name,
      textPropertyObjectId: $documentSpan[0].dataset.objectId,
    })

    drawerStore.openDrawer({
      direction: 'right',
      content: RightDrawerContent.FileAttachments,
    })
  }

  clearActiveFileElementIdentifier() {
    if (!this.activeFileElementIdentifier) {
      return
    }

    const fileLabelElement = this.getActiveFileLabelElement()

    this.activeFileElementIdentifier = null

    if (!fileLabelElement) {
      return
    }

    fileLabelElement.classList.remove(...activeFileLabelClasses)
  }

  resetActiveFileElement() {
    this.setActiveFileElementIdentifier(this.activeFileElementIdentifier)
  }

  getActiveFileLabelElement() {
    if (!this.activeFileElementIdentifier) {
      return
    }

    const [name, objectId] =
      this.activeFileElementIdentifier.split(PROPS_SEPARATOR)

    return this.liveInputs.$container[0].querySelector(
      `[data-name="${name}"][data-object-id="${objectId}"] [data-type="file_label"]`
    )
  }

  setActiveFileElementIdentifier(uniqueIdentifier) {
    this.clearActiveFileElementIdentifier()

    if (!uniqueIdentifier) {
      return
    }

    this.activeFileElementIdentifier = uniqueIdentifier
    const fileLabelElement = this.getActiveFileLabelElement()

    if (!fileLabelElement) {
      return
    }

    fileLabelElement.classList.add(...activeFileLabelClasses)
  }

  openDocumentSelect(event) {
    //Try to remove this when live-inputs & documentSelect is no longer Jquery dependent
    const $documentSpan = $(event.currentTarget)

    window.modalCitizen.openDocumentSelectModal({
      firmId: Silverfin.currentFirmId,
      companyId: Silverfin.currentCompanyId,
      options: {
        type: 'file_collection',
        locked: this.locked,
        attachmentCollectionValue: $documentSpan.data('value') || [],
        success: this.updateAttachments.bind(this, $documentSpan),
        download_documents: () => {
          window.location.href =
            Silverfin.Routes.download_documents_text_properties_path(
              this.paramsForDocumentsDownload($documentSpan[0])
            )
        },
      },
    })

    // We set the active element to this, mainly to reset the potential previously active text_input
    return this.liveInputs.setActiveElement($documentSpan)
  }

  updateAttachments(
    $documentSpan,
    documents,
    deletedDocuments,
    successCallback,
    errorCallback
  ) {
    if (this.locked) return

    const maxSize = $documentSpan[0].dataset.maxSize

    const processedDocuments = documents.map((documentData) => {
      if (documentData.linkType === 'ExternalLink') {
        return {
          external_link_attributes: {
            ...this.getDocumentAttributes(documentData),
          },
        }
      } else {
        return {
          document_attributes: {
            ...this.getDocumentAttributes(documentData),
            ...(maxSize && { max_size: maxSize }),
          },
        }
      }
    }, this)

    for (const { documentId, documentType } of deletedDocuments) {
      if (documentType === 'ExternalLink') {
        processedDocuments.push({
          external_link_attributes: {
            id: documentId,
            delete: '1',
          },
        })
      } else {
        processedDocuments.push({
          document_attributes: {
            document_id: documentId,
            delete: '1',
          },
        })
      }
    }

    this.liveInputs.update($documentSpan, {
      value: processedDocuments,
      submit: true,
      successCallback,
      errorCallback,
      clearDataOnReject: true,
    })
  }

  teardown() {
    window.eventHub.$off('drawer:animation-end', this.handleDrawerClose)
    window.eventHub.$off(
      'attachments-sidebar:update-attachments',
      this.handleUpdateAttachmentsEvent
    )
    this.liveInputs.off('click', this.inputFilter(), this.clickEventHandler)
  }
}
